/**
 * Component to handle Wealthica callback processing
 * 
 * Detects and processes callbacks from the Wealthica Connect flow.
 */

import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle2, XCircle } from 'lucide-react';
import Spinner from '@/components/ui/Spinner';
import Image from '@/components/ui/image';
import { useWealthicaCallbackOrchestrator } from '../hooks/useWealthicaCallbackOrchestrator';

// Prefer toast-only UX during OAuth callback progression (UI hidden).
const ENABLE_CALLBACK_UI = false;

/**
 * Component that processes Wealthica callbacks
 * 
 * @returns JSX.Element
 */
const Wealthica2CallbackHandler: React.FC = () => {
  const { shouldRender, shouldShowUI, displayState, instMeta, institutionLabel, status, error } =
    useWealthicaCallbackOrchestrator({ enableUI: ENABLE_CALLBACK_UI });

  if (!shouldRender) return null;
  if (!shouldShowUI) return null;

  return (
    <Card className="border-muted bg-background/60">
      <CardContent className="py-4 px-4">
        {displayState === 'connecting' && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Spinner size="md" className="text-muted-foreground" ariaLabel="Processing connection" />
            {instMeta?.logo ? (
              <Image src={instMeta.logo} alt={instMeta?.name || institutionLabel || 'Institution'} width={16} height={16} className="rounded w-4 h-4" instant />
            ) : null}
            <span>
              Processing {(instMeta?.name || institutionLabel) ? `${instMeta?.name || institutionLabel} ` : ''}connection…
            </span>
          </div>
        )}
        {displayState === 'error' && (
          <div className="flex items-center gap-2 text-sm text-destructive">
            <XCircle className="h-4 w-4" />
            <span>Connection failed{error ? `: ${String(error)}` : ''}</span>
          </div>
        )}
        {displayState === 'success' && (
          <div className="flex items-center gap-2 text-sm text-green-600">
            <CheckCircle2 className="h-4 w-4" />
            <span>Connected successfully</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default Wealthica2CallbackHandler;
